import { push, LOCATION_CHANGE } from 'react-router-redux'
import decodeJWT from 'jwt-decode'
import cookie from 'js-cookie'
import lscache from 'lscache'

import { fetchFavouriteAlbums } from '../redux/albums'
import { fetchUsers, UPDATE_USER_REQUEST } from '../redux/users'
import { fetch } from '../utils/fetch'

import {
  ENDPOINT_LOGIN,
  ENDPOINT_SHARING,
  ENDPOINT_REFRESH_TOKENS,
  ENDPOINT_REFRESH_AWS,
  AUTH_COOKIE_KEY,
  AUTH_COOKIE_EXPIRY,
  REFRESH_TOKENS_DELAY,
  SETTINGS_CACHE_KEY
} from '../constants'

const LOGIN_REQUEST = 'blayzr/auth/LOGIN_REQUEST'
export const LOGIN_SUCCESS = 'blayzr/auth/LOGIN_SUCCESS'
const LOGIN_FAILURE = 'blayzr/auth/LOGIN_FAIL'
export const LOGOUT = 'blayzr/auth/LOGOUT'
export const REFRESH_TOKENS_REQUEST = 'blayzr/auth/REFRESH_TOKENS_REQUEST'

const initialState = {
  token: null,
  user: null,
  aws: null,
  authenticated: false,
  authenticating: false,
  refreshing: false,
  error: null
}

export default function reducer (state = initialState, action = {}) {
  const { type, payload } = action
  switch (type) {
    case LOGIN_REQUEST:
      return {
        ...state,
        authenticating: true,
        error: null
      }
    case LOGIN_SUCCESS:
      return {
        ...state,
        token: payload.token,
        user: payload.user,
        aws: payload.aws,
        no_download: payload.no_download,
        limit_to_path: payload.limit_to_path,
        authenticated: true,
        authenticating: false
      }
    case LOGIN_FAILURE:
      return {
        ...state,
        token: null,
        user: null,
        authenticated: false,
        authenticating: false,
        error: payload.error
      }
    case REFRESH_TOKENS_REQUEST:
      return {
        ...state,
        refreshing: true
      }
    case UPDATE_USER_REQUEST:
      const user = payload.entities.users[state.user.id]
      if (user) {
        return {
          ...state,
          user: {
            ...state.user,
            original_settings: JSON.parse(user.settings)
          }
        }
      }
      return state
    default:
      return state
  }
}

export function loginRequest () {
  return {
    type: LOGIN_REQUEST
  }
}

let lastLoggedInTime
let logoutCheckTimeout

export function loginSuccess (data) {
  return (dispatch, getState) => {
    cookie.set(AUTH_COOKIE_KEY, data, {
      expires: new Date(+new Date() + 1000 * AUTH_COOKIE_EXPIRY)
      // TODO: secure: true
    })
    const user = decodeJWT(data.auth)
    user.original_settings = JSON.parse(user.settings)
    user.settings = {
      ...JSON.parse(user.global_settings),
      ...JSON.parse(user.settings),
      ...lscache.get(SETTINGS_CACHE_KEY)
    }
    dispatch({
      type: LOGIN_SUCCESS,
      payload: {
        token: data.auth,
        user: {
          ...user,
          // Append virtual library to user library list
          libraries: user.libraries.concat({
            library: {
              id: 0,
              library_token: `virtual_${user.id}`
            }
          })
        },
        aws: data.aws,
        no_download: data.no_download,
        limit_to_path: data.path
      }
    })
    const { albums, users } = getState()
    if (!albums.favourites.data) {
      dispatch(fetchFavouriteAlbums())
    }
    if (!users.data) {
      dispatch(fetchUsers())
    }
    lscache.setBucket(user.email)

    lastLoggedInTime = +new Date()
    logoutCheck(dispatch, getState)
  }
}

function logoutCheck (dispatch, getState) {
  clearTimeout(logoutCheckTimeout)
  const route = getState().routing.locationBeforeTransitions.pathname
  const isPresenting = /^\/present/.test(route)
  const isArrangingOrchestra = /^\/orchestras/.test(route)
  const shouldCheck = +new Date() - lastLoggedInTime > 10 * 60 * 1000 // If last login was over 10 minutes ago
  if (shouldCheck && !isPresenting && !isArrangingOrchestra) {
    dispatch(logout())
  } else {
    logoutCheckTimeout = setTimeout(() => {
      logoutCheck(dispatch, getState)
    }, 5000)
  }
}

export function loginFailure (error) {
  return {
    type: LOGIN_FAILURE,
    payload: { error }
  }
}

export function logout () {
  cookie.remove(AUTH_COOKIE_KEY)
  return {
    type: LOGOUT
  }
}

export function refreshTokensRequest () {
  return {
    type: REFRESH_TOKENS_REQUEST
  }
}

export function login (email, password, redirect) {
  return dispatch => {
    dispatch(loginRequest())

    return fetch(ENDPOINT_LOGIN, {
      method: 'post',
      body: { email, password }
    }).then(
      data => onLoginResponse(data, dispatch, redirect),
      e => onLoginError(e, dispatch)
    )
  }
}

export function shareLogin (code) {
  return dispatch => {
    const endpoint = ENDPOINT_SHARING.replace(':code', code)
    dispatch(loginRequest())
    fetch(endpoint).then(
      data => onLoginResponse(data, dispatch),
      e => onLoginError(e, dispatch)
    )
  }
}

export function loginAndJoinOrchestra (code) {
  return dispatch => {
    const endpoint = ENDPOINT_SHARING.replace(':code', code)
    dispatch(loginRequest())
    fetch(endpoint).then(
      data => onLoginResponse(data, dispatch),
      e => onLoginError(e, dispatch)
    )
  }
}

function onLoginResponse(data, dispatch, redirect) {
  try {
    decodeJWT(data.records.auth) // Validate token
    dispatch(loginSuccess(data.records))
    if (data.records.path) {
      dispatch(push(data.records.path))
    } else if (redirect) {
      dispatch(push(redirect))
    }
  } catch (e) {
    dispatch(loginFailure({
      response: {
        status: 403,
        statusText: 'Invalid token'
      }
    }))
  }
}

function onLoginError (e, dispatch) {
  console.error(e)
  dispatch(loginFailure(e))
  throw e
}

export function refreshTokens () {
  return (dispatch, getState) => {
    const { token } = getState().auth
    if (!token) {
      return
    }
    const options = { method: 'post', token }
    return Promise.all([
      fetch(ENDPOINT_REFRESH_TOKENS, options, dispatch),
      fetch(ENDPOINT_REFRESH_AWS, options, dispatch)
    ]).then(() => {
      lastLoggedInTime = +new Date()
    })
    // dispatch(refreshTokensRequest())
    // return fetch(ENDPOINT_REFRESH_TOKENS, { token }, dispatch).then(data => {
    //   dispatch(loginSuccess(data.records))
    // })
  }
}

// Redux middleware to refresh tokens a short time after a change to the store
// Thunks and the refresh actions themselves are ignored to prevent an infinite loop
let refreshTokenTimeout
export function tokenRefreshMiddleware (store) {
  return next => action => {
    if (
      store.getState().auth.authenticated &&
      store.getState().auth.user.id !== 0 &&
      typeof action !== 'function' &&
      action.type !== REFRESH_TOKENS_REQUEST &&
      action.type !== LOGIN_SUCCESS
    ) {
      clearTimeout(refreshTokenTimeout)
      refreshTokenTimeout = setTimeout(
        () => next(refreshTokens()),
        REFRESH_TOKENS_DELAY * 1000
      )
    }
    return next(action)
  }
}

// Prevent external users from navigating anywhere
export function externalUserMiddleware (store) {
  return next => action => {
    if (action.type === LOCATION_CHANGE) {
      const state = store.getState()
      if (state.auth.user && state.auth.user.id === 0 && state.auth.limit_to_path !== action.payload.pathname) {
        next(logout())
        return
      }
    }
    return next(action)
  }
}



// WEBPACK FOOTER //
// ./src/redux/auth.js